import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import LoginMenuButton from '@/components/nav/LoginMenuButton.vue';
import LoginMenuButtonNew from '@/components/nav/LoginMenuButtonNew.vue';
import SettingsButton from '@/components/nav/static-pages/SettingsButton.vue';
import SearchSuggesterBar from '@/features/search/components/SearchSuggesterBar.vue';

import { CollectionType } from '@/enums/collection-type';
import { OnboardingStatus } from '@/enums/onboarding-status';

import { getRouteName, getUrlParamsFromFilterState } from '@/helpers/state-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { faBookmark } from '@fortawesome/free-solid-svg-icons';

import { useSearch } from '@/features/search/composables/useSearch';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import type { FilterCollection } from '@/stores/useFilterStoreTypes';
import { getNavItemVisibility } from '@/helpers/navigation-helper';
import { isEditorialLocale } from '@/routing/editorial';


export default /*#__PURE__*/_defineComponent({
  __name: 'AppHeader',
  emits: ['tabChange'],
  setup(__props, { emit: emits }) {

const FreemiumFilterBadge = () =>
	import(/* webpackChunkName: "freemium-filter-badge" */ '@/components/filter-bar/FreemiumFilterBadge.vue');


const { subgenresList, providersByShortName, genresByShortName } = storeToRefs(useConstantStore());
const { filters } = useFilterStore();
const { webLocale } = storeToRefs(useLanguageStore());
const { activeRoute, lastUrlMetadata, lastActiveListsCollectionType, lastActiveCollectionType } = storeToRefs(
	useRoutingStore()
);
const { onboardingStatus, isLoggedIn, isPremium } = storeToRefs(useUserStore());

const { isSearchSuggesterOpen, setSearchSuggesterVisibility } = useSearch();

// Navigation visibility
const shouldShowWatchInHeader = computed(() => getNavItemVisibility(webLocale.value, 'watch', 'header').desktop);
const shouldShowGuideInHeader = computed(() => isEditorialLocale(webLocale.value));
const shouldShowSportsInHeader = computed(() => getNavItemVisibility(webLocale.value, 'sports', 'header').desktop);
const shouldShowGuideInHeaderMobile = computed(() => getNavItemVisibility(webLocale.value, 'guide', 'header').mobile);

const isNewTab = computed(() => {
	const newPageRoutes = ['app.titles.new', 'app.titles.upcoming', 'app.titles.pricedrops'];
	return newPageRoutes.find(route => (activeRoute.value?.name || '').startsWith(route));
});

const isNetflixStatisticPage = computed(() => activeRoute.value?.name === 'app.landingpage.statistics');

const isHome = computed(() => activeRoute.value?.name === 'app.home');

const isGlobal = computed(() => onboardingStatus.value === OnboardingStatus.GLOBAL);

const isUnsupportedCountry = computed(() => onboardingStatus.value === OnboardingStatus.UNSUPPORTED_COUNTRY);

const isStreamingCharts = computed(() => activeRoute.value?.name === 'app.streaming-charts');

const isEditorial = computed(() => (activeRoute.value?.name || '').startsWith('app.editorial'));

const logoSrc = computed(() => {
	const file = `JustWatch-logo-${isGlobal.value ? 'large' : 'small'}`;
	return {
		png: `/${ASSETS_DIR}/img/logo/${file}.png`,
		webp: `/${ASSETS_DIR}/img/logo/${file}.webp`,
	};
});

const listsCollectionTypeToNavigate = computed(() => {
	if (lastActiveListsCollectionType.value) {
		return lastActiveListsCollectionType.value;
	}

	return isLoggedIn.value ? CollectionType.MY_LISTS : CollectionType.PUBLIC_LISTS;
});

/**
 * with these parameters we can always link to the correct url for each collectionType page.
 * in some cases we want to overwrite the currentFilters of the passed CollectionType, that's what `filterOverwrite` handles.
 */
function routeParams(collectionType: CollectionType, filterOverwrite?: FilterCollection): any {
	const isNewPageCollectionType = [
		CollectionType.COMING_SOON,
		CollectionType.LEAVING_SOON,
		CollectionType.PRICEDROPS,
	].includes(collectionType);

	const filterCollectionType = isNewPageCollectionType ? CollectionType.NEW : collectionType;
	const _filters = filterOverwrite ?? filters(filterCollectionType);
	const routeName = getRouteName(collectionType, _filters, subgenresList.value);
	const params = getUrlParamsFromFilterState(
		routeName,
		_filters,
		providersByShortName.value,
		genresByShortName.value,
		collectionType,
		activeRoute.value?.query || {},
		subgenresList.value
	);

	// Note: list_id query param needs to be deleted when navigating to the Lists tab root level, since we are
	// using it on Lists tab to determinate if list details page is opened
	if (params.query.list_id) {
		delete params.query.list_id;
	}

	return { name: routeName, ...params };
}

async function onTabChange(label: string) {
	emits('tabChange');
	TrackingHelper.trackEvent('userinteraction', {
		action: 'tabchange',
		label,
		useLastRoute: true,
	});
}

function trackLogoClick() {
	// If you click the JW Logo on your first page visit, there won't be a "last route"
	// This had forced the page type logic in the tracker to depend on useRoute or $route,
	// both of which depend on a Vue instance which isn't available there (as of now).
	// Now, if you click it before navigating, it'll use the current route metadata.
	const useLastRoute = lastUrlMetadata != null;

	TrackingHelper.trackEvent('userinteraction', {
		action: 'tabchange',
		label: 'click_logo',
		useLastRoute,
	});
}

function onOverlayClick() {
	setSearchSuggesterVisibility(false);
}

return { __sfc: true,FreemiumFilterBadge, emits, subgenresList, providersByShortName, genresByShortName, filters, webLocale, activeRoute, lastUrlMetadata, lastActiveListsCollectionType, lastActiveCollectionType, onboardingStatus, isLoggedIn, isPremium, isSearchSuggesterOpen, setSearchSuggesterVisibility, shouldShowWatchInHeader, shouldShowGuideInHeader, shouldShowSportsInHeader, shouldShowGuideInHeaderMobile, isNewTab, isNetflixStatisticPage, isHome, isGlobal, isUnsupportedCountry, isStreamingCharts, isEditorial, logoSrc, listsCollectionTypeToNavigate, routeParams, onTabChange, trackLogoClick, onOverlayClick, LoginMenuButton, LoginMenuButtonNew, SettingsButton, SearchSuggesterBar, CollectionType, faBookmark }
}

})